import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ChevronLeft, Save, Loader2, Image as ImageIcon, Mail, Phone, MapPin, Clock,
  Building2, FileText, ShieldCheck, X, Upload, ExternalLink, KeyRound, Lock, Check,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
/**
 * Super-Admin Site Content editor.
 *
 * Tabs (vertical on desktop, accordion-style):
 *  1. Landing hero image — base64 (auto-compressed) OR external URL
 *  2. Footer contact details
 *  3. Privacy Policy (markdown)
 *  4. Terms of Use   (markdown)
 *
 * Saves go through PATCH /api/site-content with partial fields.
 */
const TABS = [
  { id: "hero",     label: "Hero Image",       icon: ImageIcon },
  { id: "footer",   label: "Footer Contact",   icon: Building2 },
  { id: "privacy",  label: "Privacy Policy",   icon: ShieldCheck },
  { id: "terms",    label: "Terms of Use",     icon: FileText },
  { id: "email",    label: "Platform Email",   icon: Mail },
  { id: "password", label: "Change Password",  icon: KeyRound },
];

export default function SiteContentPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("hero");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doc, setDoc] = useState(null);
  const [dirty, setDirty] = useState(false);

  // Load current document on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // include_hero=true so the SA preview shows the currently-saved image
        const { data } = await api.get("/site-content?include_hero=true");
        if (alive) setDoc(data);
      } catch (e) {
        if (alive) toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const patch = (updates) => {
    setDoc((d) => ({ ...d, ...updates }));
    setDirty(true);
  };
  const patchFooter = (updates) => {
    setDoc((d) => ({ ...d, footer: { ...(d?.footer || {}), ...updates } }));
    setDirty(true);
  };

  const save = async () => {
    if (!doc) return;
    setSaving(true);
    try {
      const body = {
        hero_image_base64: doc.hero_image_base64 ?? "",
        hero_image_url:    doc.hero_image_url ?? "",
        footer:            doc.footer,
        privacy_policy_md: doc.privacy_policy_md ?? "",
        terms_of_use_md:   doc.terms_of_use_md ?? "",
      };
      const { data } = await api.patch("/site-content", body);
      setDoc(data);
      setDirty(false);
      toast.success("Site content saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // ----------------- Hero upload (auto-compressed) -----------------
  const onHeroUpload = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await compressImage(file, { maxKB: 400, maxDim: 1600 });
      patch({ hero_image_base64: dataUrl, hero_image_url: "" });
      toast.success("Hero image compressed and ready. Click Save to publish.");
    } catch (e) {
      toast.error("Could not process image: " + e.message);
    }
  };

  if (!user || (user.role !== "super_admin" && user.role !== "sub_super_admin")) {
    return (
      <>
        <div className="p-10 text-center text-slate-500">Super admin only.</div>
      </>
    );
  }
  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center text-slate-500 py-20">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading site content…
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-5" data-testid="site-content-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs text-slate-400 uppercase font-heading">Super admin</p>
            <h1 className="font-heading text-3xl font-bold text-foreground">Settings</h1>
            <p className="text-sm text-brand-inkMuted mt-1">Edit landing hero, footer, legal pages, and your password.</p>
          </div>
          <div className="flex items-center gap-2">
            {tab !== "password" && (
              <>
                {dirty && <span className="text-xs text-slate-500">Unsaved changes</span>}
                <Link to="/" className="text-sm text-slate-500 hover:text-foreground inline-flex items-center gap-1" target="_blank" data-testid="preview-link">
                  <ExternalLink className="w-4 h-4" /> Preview
                </Link>
                <Button onClick={save} disabled={!dirty || saving}
                  className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] h-10 px-5 text-white font-heading font-semibold shadow-floating"
                  data-testid="sc-save-btn">
                  {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="space-y-5">
          {/* Tabs (horizontal row, top) */}
          <nav className="flex flex-wrap gap-2" aria-label="Site content sections">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl font-heading font-semibold text-sm transition-all ${
                  tab === t.id
                    ? "bg-[#2563EB] text-white shadow-floating"
                    : "bg-white border border-slate-200/70 text-slate-600 hover:border-[#2563EB]/40"
                }`}
                data-testid={`sc-tab-${t.id}`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </nav>

          {/* Panels */}
          <div>
            {tab === "hero" && <HeroPanel doc={doc} patch={patch} onUpload={onHeroUpload} />}
            {tab === "footer" && <FooterPanel doc={doc} patch={patchFooter} />}
            {tab === "privacy" && <MarkdownPanel
              title="Privacy Policy"
              description="Markdown is supported. Leave empty to use the default content shipped with SchoolBoss."
              value={doc.privacy_policy_md || ""}
              onChange={(v) => patch({ privacy_policy_md: v })}
              previewHref="/privacy"
            />}
            {tab === "terms" && <MarkdownPanel
              title="Terms of Use"
              description="Markdown is supported. Leave empty to use the default content shipped with SchoolBoss."
              value={doc.terms_of_use_md || ""}
              onChange={(v) => patch({ terms_of_use_md: v })}
              previewHref="/terms"
            />}
            {tab === "email" && <PlatformEmailPanel />}
            {tab === "password" && <PasswordPanel />}
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Hero panel
// ---------------------------------------------------------------------------
function HeroPanel({ doc, patch, onUpload }) {
  const fileRef = React.useRef(null);
  const hero = doc.hero_image_base64 || doc.hero_image_url || "";
  return (
    <Card className="p-7 rounded-3xl border-0 shadow-floating">
      <h2 className="font-heading text-xl font-bold">Landing Hero Image</h2>
      <p className="text-sm text-slate-500 mt-1">
        Recommended size: <strong>1920×640</strong> (~3:1 cinematic banner). Uploaded files are auto-compressed to ≤ 400KB.
      </p>

      <div className="mt-5 rounded-2xl border border-dashed border-slate-300 overflow-hidden bg-slate-50/50 aspect-[5/3] flex items-center justify-center relative">
        {hero ? (
          <img src={hero} alt="Hero preview" className="w-full h-full object-cover" data-testid="sc-hero-preview" />
        ) : (
          <p className="text-slate-400 text-sm">No custom hero image — the default illustration is used.</p>
        )}
        {hero && (
          <button onClick={() => patch({ hero_image_base64: "", hero_image_url: "" })}
            className="absolute top-3 right-3 bg-white/95 rounded-full p-1.5 shadow-sm hover:bg-white"
            data-testid="sc-hero-remove">
            <X className="w-4 h-4 text-[#C62828]" />
          </button>
        )}
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <Button onClick={() => fileRef.current?.click()} variant="outline"
          className="rounded-full border-slate-200 h-11" data-testid="sc-hero-upload-btn">
          <Upload className="w-4 h-4 mr-1.5" /> Upload from device
        </Button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }} data-testid="sc-hero-file" />
        <div>
          <Input value={doc.hero_image_url || ""}
            onChange={(e) => patch({ hero_image_url: e.target.value, hero_image_base64: "" })}
            placeholder="…or paste an image URL"
            className="h-11 rounded-full border-slate-200"
            data-testid="sc-hero-url" />
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Footer panel
// ---------------------------------------------------------------------------
function FooterPanel({ doc, patch }) {
  const f = doc.footer || {};
  return (
    <Card className="p-7 rounded-3xl border-0 shadow-floating space-y-4">
      <div>
        <h2 className="font-heading text-xl font-bold">Footer Contact Details</h2>
        <p className="text-sm text-slate-500 mt-1">Shown at the bottom of the public landing page.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Company name" icon={Building2}>
          <Input value={f.company_name || ""} onChange={(e) => patch({ company_name: e.target.value })}
            className="rounded-2xl h-11 pl-10" data-testid="sc-footer-company" />
        </Field>
        <Field label="Tagline (footer subtitle)" icon={ImageIcon} fullWidth>
          <Input value={f.tagline || ""} onChange={(e) => patch({ tagline: e.target.value })}
            className="rounded-2xl h-11 pl-10" data-testid="sc-footer-tagline" />
        </Field>
        <Field label="Email" icon={Mail}>
          <Input value={f.email || ""} onChange={(e) => patch({ email: e.target.value })}
            className="rounded-2xl h-11 pl-10" data-testid="sc-footer-email" />
        </Field>
        <Field label="Phone" icon={Phone}>
          <Input value={f.phone || ""} onChange={(e) => patch({ phone: e.target.value })}
            className="rounded-2xl h-11 pl-10" data-testid="sc-footer-phone" />
        </Field>
        <Field label="Address" icon={MapPin} fullWidth>
          <Input value={f.address || ""} onChange={(e) => patch({ address: e.target.value })}
            className="rounded-2xl h-11 pl-10" data-testid="sc-footer-address" />
        </Field>
        <Field label="Hours" icon={Clock}>
          <Input value={f.hours || ""} onChange={(e) => patch({ hours: e.target.value })}
            className="rounded-2xl h-11 pl-10" data-testid="sc-footer-hours" />
        </Field>
      </div>
    </Card>
  );
}

const Field = ({ label, icon: Icon, children, fullWidth }) => (
  <div className={fullWidth ? "sm:col-span-2" : ""}>
    <Label className="font-heading text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="relative mt-1.5">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
      {children}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Markdown panel (Privacy + Terms)
// ---------------------------------------------------------------------------
function MarkdownPanel({ title, description, value, onChange, previewHref }) {
  return (
    <Card className="p-7 rounded-3xl border-0 shadow-floating">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold">{title}</h2>
          <p className="text-sm text-slate-500 mt-1">{description}</p>
        </div>
        {previewHref && (
          <Link to={previewHref} target="_blank" className="text-sm font-semibold text-[#2563EB] hover:underline inline-flex items-center gap-1 shrink-0">
            View page <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={20}
        placeholder={`# ${title}\n\nWrite your content in **markdown**. Supports headings, bold, italic, lists and links.\n\nLeave empty to use the default content.`}
        className="mt-4 rounded-2xl font-mono text-[13px] leading-relaxed"
        data-testid={`sc-md-${title.replace(/\s+/g, "-").toLowerCase()}`}
      />
      <p className="text-xs text-slate-400 mt-2">
        Markdown supported: <code>#</code> headings, <code>**bold**</code>, <code>*italic*</code>, <code>- lists</code>, <code>[link](url)</code>.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Password panel — self-contained card with its own save button
// ---------------------------------------------------------------------------
function PasswordPanel() {
  const [currentPw, setCurrentPw] = React.useState("");
  const [newPw, setNewPw] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (newPw !== confirm) { setError("New password and confirmation do not match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Password updated successfully.");
      setCurrentPw(""); setNewPw(""); setConfirm("");
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-7 rounded-3xl border-0 shadow-floating max-w-2xl" data-testid="sc-password-panel">
      <div className="flex items-start gap-4 mb-5">
        <span className="w-11 h-11 rounded-2xl bg-[#DBEAFE] flex items-center justify-center shrink-0">
          <ShieldCheck className="w-6 h-6 text-[#2563EB]" />
        </span>
        <div>
          <h2 className="font-heading text-xl font-bold">Change Password</h2>
          <p className="text-sm text-slate-500 mt-1 leading-relaxed">
            Pick a strong password you don't use anywhere else. You'll stay signed in on this device after the change.
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="sc-pw-form">
        <PwField label="Current password" value={currentPw} onChange={setCurrentPw} testid="sc-pw-current" />
        <PwField label="New password" value={newPw} onChange={setNewPw} testid="sc-pw-new"
          hint="At least 8 characters. Mix letters, numbers and symbols for best results." />
        <PwField label="Confirm new password" value={confirm} onChange={setConfirm} testid="sc-pw-confirm" />

        {error && (
          <div className="rounded-2xl bg-[#FFEBEE] text-[#C62828] px-4 py-3 text-sm" data-testid="sc-pw-error">
            {error}
          </div>
        )}

        <Button type="submit" disabled={saving || !currentPw || !newPw || !confirm}
          className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11 px-6 font-heading font-semibold shadow-floating"
          data-testid="sc-pw-save-btn">
          {saving
            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Updating…</>
            : <><Check className="w-4 h-4 mr-1.5" /> Update password</>}
        </Button>
      </form>
    </Card>
  );
}

const PwField = ({ label, value, onChange, hint, testid }) => (
  <div>
    <Label className="font-heading text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="relative mt-1.5">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
      <Input type="password" value={value} onChange={(e) => onChange(e.target.value)}
        required minLength={8}
        className="rounded-2xl h-12 pl-10"
        data-testid={testid} />
    </div>
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

// ---------------------------------------------------------------------------
// Platform Email panel — the Gmail account SchoolBoss uses to send password
// reset emails. Super Admin only.
// ---------------------------------------------------------------------------
function PlatformEmailPanel() {
  const [cfg, setCfg] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [gmail, setGmail] = React.useState("");
  const [sender, setSender] = React.useState("SchoolBoss");
  const [appPw, setAppPw] = React.useState("");
  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/platform-email-config");
      setCfg(data);
      setGmail(data.gmail_address || "");
      setSender(data.sender_display_name || "SchoolBoss");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!gmail.trim()) { toast.error("Gmail address is required"); return; }
    if (!cfg?.configured && !appPw.trim()) { toast.error("App Password is required for the first setup"); return; }
    setSaving(true);
    try {
      await api.put("/super-admin/platform-email-config", {
        gmail_address: gmail.trim(),
        sender_display_name: sender.trim() || "SchoolBoss",
        gmail_app_password: appPw,
      });
      toast.success(cfg?.configured ? "Platform email updated" : "Platform email saved");
      setAppPw(""); setEditing(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <Card className="p-7 rounded-3xl border-0 shadow-floating text-slate-500">Loading…</Card>;

  return (
    <Card className="p-7 rounded-3xl border-0 shadow-floating" data-testid="sc-platform-email-card">
      <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Mail className="w-5 h-5 text-brand-purple" /> Platform Email</h2>
      <p className="text-sm text-slate-500 mt-1 mb-5">
        The Gmail account SchoolBoss uses for password reset emails. Use a <strong>Google App Password</strong> (16 chars) — generate one at{" "}
        <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-brand-purple underline">myaccount.google.com/apppasswords</a> (2-Step Verification must be ON on the Gmail account).
      </p>

      {cfg?.configured && !editing ? (
        <div className="space-y-2 text-sm">
          <div className="rounded-2xl bg-[#E8F5E9] border border-[#A5D6A7] px-3 py-2 inline-flex items-center gap-1.5 text-[#2E7D32] font-heading text-xs">
            <Check className="w-3.5 h-3.5" /> Configured · {cfg.masked_address}
          </div>
          <div className="mt-3"><span className="text-brand-inkMuted">From:</span> <strong className="text-foreground">{cfg.gmail_address}</strong></div>
          <div><span className="text-brand-inkMuted">Display name:</span> <strong className="text-foreground">{cfg.sender_display_name}</strong></div>
          <div className="pt-3">
            <Button variant="outline" onClick={() => setEditing(true)} className="rounded-2xl" data-testid="sc-platform-email-edit">
              <KeyRound className="w-4 h-4 mr-1.5" /> Update credentials
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="font-heading">Gmail address <span className="text-[#C62828]">*</span></Label>
            <Input type="email" value={gmail} onChange={(e) => setGmail(e.target.value)} placeholder="schoolboss@gmail.com" className="rounded-2xl h-11 mt-1.5" autoComplete="off" data-testid="sc-platform-email-address" />
          </div>
          <div>
            <Label className="font-heading">Sender display name</Label>
            <Input value={sender} onChange={(e) => setSender(e.target.value)} placeholder="SchoolBoss" className="rounded-2xl h-11 mt-1.5" autoComplete="off" data-testid="sc-platform-email-sender" />
          </div>
          <div className="sm:col-span-2">
            <Label className="font-heading">App Password {cfg?.configured ? <span className="text-brand-inkMuted text-xs">(leave blank to keep current)</span> : <span className="text-[#C62828]">*</span>}</Label>
            <Input type="password" value={appPw} onChange={(e) => setAppPw(e.target.value)} placeholder="16-character app password" className="rounded-2xl h-11 mt-1.5" autoComplete="new-password" data-testid="sc-platform-email-apppw" />
          </div>
          <div className="sm:col-span-2 flex gap-2 justify-end">
            {cfg?.configured && <Button type="button" variant="outline" onClick={() => { setEditing(false); setAppPw(""); }} className="rounded-2xl">Cancel</Button>}
            <Button type="submit" disabled={saving} className="rounded-2xl bg-brand-purple text-white" data-testid="sc-platform-email-save">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : "Save Platform Email"}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

